import { useState, useEffect } from "react";
import "./App.css";
import { checkAuth, logout } from "./api";

import LoginPage from "./components/LoginPage";
import ConfigPage from "./components/ConfigPage";
import SearchPage from "./components/SearchPage";
import ComparePanel from "./components/ComparePanel";
import NewsPanel from "./components/NewsPanel";
import TrendsPanel from "./components/TrendsPanel";
import HealthPanel from "./components/HealthPanel";
import PipelinePanel from "./components/PipelinePanel";
import TriagePanel from "./components/TriagePanel";
import ProductsPanel from "./components/ProductsPanel";
import QueriesPanel from "./components/QueriesPanel";
import ReviewPanel from "./components/ReviewPanel";
import ClassificationBar from "./components/ClassificationBar";

// Map URL pathname → view name. /cve/... stays on the search view (SearchPage handles it).
const PATH_TO_VIEW = {
  "/news": "news",
  "/models": "compare",
  "/trends": "trends",
  "/health": "health",
  "/pipeline": "pipeline",
  "/triage": "triage",
  "/products": "products",
  "/queries": "queries",
  "/review": "review",
  "/config": "config",
};
const VIEW_TO_PATH = {
  search: "/",
  compare: "/models",
  trends: "/trends",
  health: "/health",
  pipeline: "/pipeline",
  triage: "/triage",
  products: "/products",
  queries: "/queries",
  review: "/review",
  news: "/news",
  config: "/config",
};

function _viewFromPath(pathname) {
  if (PATH_TO_VIEW[pathname]) return PATH_TO_VIEW[pathname];
  if (pathname.startsWith("/health")) return "health"; // /health/<tab> deep links
  if (pathname.startsWith("/pipeline")) return "pipeline"; // /pipeline/<tab> deep links
  if (pathname.startsWith("/triage")) return "triage"; // /triage/<jobId> deep links
  if (pathname.startsWith("/review")) return "review"; // /review/<cveId> deep links
  return "search"; // default — includes /, /cve/<id>, anything else
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState(() => _viewFromPath(window.location.pathname));
  const [searchKey, setSearchKey] = useState(0);
  const [reviewKey, setReviewKey] = useState(0);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "dark");

  // Check auth on mount
  useEffect(() => {
    checkAuth()
      .then((data) => { setUser(data.user); setIsAdmin(!!data.is_admin); })
      .catch(() => { setUser(null); setIsAdmin(false); })
      .finally(() => setAuthChecked(true));
  }, []);

  // Sync view with browser back/forward navigation
  useEffect(() => {
    const onPopState = () => setCurrentView(_viewFromPath(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = (view) => {
    const path = VIEW_TO_PATH[view] || "/";
    if (window.location.pathname !== path) {
      window.history.pushState(null, "", path);
    }
    setCurrentView(view);
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme === "light" ? "light" : "");
    localStorage.setItem("theme", theme);
  }, [theme]);

  if (!authChecked) return <><ClassificationBar /><div className="loading">Loading...</div></>;
  if (!user) return <><ClassificationBar /><LoginPage onLogin={(u) => { setUser(u); }} /></>;
  if (error) return <><ClassificationBar /><div className="error">Error: {error}</div></>;

  return (
    <>
    <ClassificationBar />
    <div className="app">
      <div className="search-bg-overlay" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/tharros-t.png)` }} />
      <header className="app-header">
        <h1><img src="/icon.png" alt="" style={{ height: 32, verticalAlign: "middle", marginRight: 8 }} />VINCE-NT VulScope</h1>
        <nav className="header-nav">
          <button
            className={`nav-btn ${currentView === "search" ? "nav-btn-active" : ""}`}
            onClick={() => { navigate("search"); setSearchKey(k => k + 1); }}
          >
            Search
          </button>
          <button
            className={`nav-btn ${currentView === "compare" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("compare")}
          >
            Models
          </button>
          <button
            className={`nav-btn ${currentView === "trends" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("trends")}
          >
            Trends
          </button>
          <button
            className={`nav-btn ${currentView === "news" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("news")}
          >
            News
          </button>
          <button
            className={`nav-btn ${currentView === "triage" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("triage")}
          >
            Triage
          </button>
          <button
            className={`nav-btn ${currentView === "products" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("products")}
          >
            Products
          </button>
          <button
            className={`nav-btn ${currentView === "queries" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("queries")}
          >
            Queries
          </button>
          <button
            className={`nav-btn ${currentView === "review" ? "nav-btn-active" : ""}`}
            onClick={() => { navigate("review"); setReviewKey(k => k + 1); }}
          >
            Review
          </button>
          <button
            className={`nav-btn ${currentView === "health" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("health")}
          >
            Health
          </button>
          <button
            className={`nav-btn ${currentView === "pipeline" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("pipeline")}
          >
            Pipeline
          </button>
          <button
            className={`nav-btn ${currentView === "config" ? "nav-btn-active" : ""}`}
            onClick={() => navigate("config")}
          >
            Config
          </button>
        </nav>
        <div className="header-right">
          <button
            className="flush-btn theme-toggle"
            onClick={() => setTheme(t => t === "dark" ? "light" : "dark")}
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          >
            {theme === "dark" ? "☀" : "☾"}
          </button>
          <span className="meta">{user}</span>
          <button className="flush-btn" onClick={() => logout().then(() => setUser(null))}>
            Logout
          </button>
        </div>
      </header>


      {currentView === "search" && <SearchPage key={searchKey} />}

      {currentView === "compare" && <ComparePanel />}

      {currentView === "trends" && <TrendsPanel />}

      {currentView === "news" && <NewsPanel isAdmin={isAdmin} />}

      {currentView === "health" && <HealthPanel />}

      {currentView === "pipeline" && <PipelinePanel />}

      {currentView === "triage" && <TriagePanel />}

      {currentView === "products" && <ProductsPanel />}

      {currentView === "queries" && <QueriesPanel />}

      {currentView === "review" && <ReviewPanel key={reviewKey} />}

      {currentView === "config" && <ConfigPage />}
    </div>
    </>
  );
}

export default App;
